import React, { useState, useEffect } from 'react';
import { useHistory, useLocation, useRouteMatch, Switch, Route } from "react-router-dom";
import { GelThemeProvider } from '@tal-gel/theming';
import { GelContainerLite, GelColumnLayout, GelRowLayout, GelScreenDetector } from '@tal-gel/components';
import { policyRecordStyle, toastDiv, recordDetailsSection, policyRecordSection, headerOverviewSection, overviewSection, headerSection, toastFail } from '../components/Shared/Styles';
import 'bootstrap/dist/css/bootstrap.min.css';

import GetBeneficiaries from '../services/GetBeneficiaries';
import GetCoverDetails from '../services/GetCoverDetails';
import GetPaymentDetails from '../services/GetPaymentDetails';
import GetPolicyDetails from '../services/GetPolicyDetails';
import GetPolicyExclusion from '../services/GetPolicyExclusion';
import GetPolicyLives from '../services/GetPolicyLives';
import GetPolicySummary from '../services/GetPolicySummary';


import ActionButtons from '../components/Overview/ActionButtons';
import Beneficiaries from '../components/Beneficiaries/Beneficiaries';
import Header from '../components/Header/Header';
import Overview from '../components/Overview/Overview';
import PaymentDetails from '../components/PaymentDetails/PaymentDetails';
import PolicyExclusionTab from '../components/PolicyExclusion/PolicyExclusionTab';
import RecordDetails from '../components/RecordDetails/RecordDetails';
import RequestPolicyDocuments from '../components/RequestPolicyDocuments/RequestPolicyDocuments';
import RequestEBQ from '../components/RequestEBQ/RequestEBQ';
import AlterPolicy from '../components/AlterPolicy/AlterPolicy';
import ViewCorrespondence from '../components/Correspondences/ViewCorrespondence';
import SideNav from '../components/SideNav/SideNav';
import ToastMessage from '../components/Shared/ToastMessage';
import Spinner from '../components/Spinner/Spinner';
import CertificateOfCurrency from './CertificateOfCurrency';
import PolicySummary from './PolicySummary';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

export default function PolicyRecord() {
    let query = useQuery();
    const history = useHistory();
    const location = useLocation();
    let { url } = useRouteMatch();

    function toNonEmptyString(value) {
        if (value === undefined || value === null || value === "") {
            return "";
        }

        return value.toString();
    }

    function normalizeValue(value) {
        const stringValue = toNonEmptyString(value).trim();

        if (!stringValue) {
            return "";
        }

        const normalizedStringValue = stringValue.toLowerCase();

        if (normalizedStringValue === "undefined" || normalizedStringValue === "null") {
            return "";
        }

        return stringValue;
    }

    function hasUsableQueryValue(queryParams, key) {
        return normalizeValue(queryParams.get(key)) !== "";
    }

    const [policyLives, setPolicyLives] = useState({});
    const [policies, setPolicies] = useState([]);
    const [policyDetails, setPolicyDetails] = useState({});
    const [coverDetails, setCoverDetails] = useState({});
    const [paymentDetails, setPaymentDetails] = useState({});
    const [policyExclusion, setPolicyExclusion] = useState({});
    const [beneficiaries, setBeneficiaries] = useState({});
    const [spinnerLoading, setSpinnerLoading] = useState(false);

    const [policyStatus, setPolicyStatus] = useState();
    const [distributorClientNo, setDistributorClientNo] = useState(normalizeValue(query.get("distributorClientNo")));
    const [policyNumber] = useState(query.get("policyNumber"));
    const [distributorNo] = useState(query.get("distributorNo"));
    const [clientId, setClientId] = useState(normalizeValue(query.get("clientId")));
    const [appRefNo, setAppRefNo] = useState(normalizeValue(query.get("appRefNo")));
    const [policyOrigin] = useState(query.get("policyOrigin"));
    const [productCode, setProductCode] = useState("");

    const [adviserEmail] = useState(
        (document.getElementById('adviserEmail')) ?
            document.getElementById('adviserEmail').innerHTML :
            "");
    const [isStaff] = useState(
        (document.getElementById('isStaff') && document.getElementById('isStaff').innerHTML.toLowerCase() === 'true') ?
            true :
            false);
    const [tlisQuoteFormURL] = useState(
        (document.getElementById('tlisQuoteFormURL')) ?
            document.getElementById('tlisQuoteFormURL').innerHTML :
            "");
    const [certificateOfCurrency] = useState(
        (document.getElementById('certificateOfCurrency') && document.getElementById('certificateOfCurrency').innerHTML.toLowerCase() === 'true') ?
            true : false);

    const [toastBreadcrumb, setToastBreadcrumb] = useState(false);
    const [toastPolicyDetails, setToastPolicyDetails] = useState(false);
    const [toastTitleToast, setToastTitleToast] = useState(false);
    const [toastRecordDetails, setToastRecordDetails] = useState(false);
    const [toastCoverDetails, setToastCoverDetails] = useState(false);
    const [toastPaymentDetails, setToastPaymentDetails] = useState(false);
    const [toastPolicyExclusion, setToastPolicyExclusion] = useState(false);
    const [toastBeneficiaries, setToastBeneficiaries] = useState(false);
    const [refreshState, setRefreshState] = useState(false);

    const isRequestExistingBusinessQuotePage = location.pathname.endsWith('/request-existing-business-quote');
    const shouldResolveDistributorClientFromPolicyLives = !distributorClientNo;

    const toggleToastBreadcrumb = () => setToastBreadcrumb(!toastBreadcrumb);
    const toggleToastPolicyDetails = () => setToastPolicyDetails(!toastPolicyDetails);
    const toggleToastTitleToast = () => setToastTitleToast(!toastTitleToast);
    const toggleToastRecordDetails = () => setToastRecordDetails(!toastRecordDetails);
    const toggleToastCoverDetails = () => setToastCoverDetails(!toastCoverDetails);
    const toggleToastPaymentDetails = () => setToastPaymentDetails(!toastPaymentDetails);
    const toggleToastPolicyExclusion = () => setToastPolicyExclusion(!toastPolicyExclusion);
    const toggleToastBeneficiaries = () => setToastBeneficiaries(!toastBeneficiaries);

    const triggerRefreshState = () => setRefreshState(!refreshState);

    function createEmptyRequestEbqFields() {
        return {
            distributorClientNo: "",
            appRefNo: "",
            lifeNo: "",
            productSeries: "",
            clientId: ""
        };
    }

    function getFirstItem(items) {
        return Array.isArray(items) && items.length > 0 ? items[0] : {};
    }

    function getNormalizedValue(source, aliases, ...fallbackSources) {
        const sources = [source, ...fallbackSources];

        for (const currentSource of sources) {
            for (const alias of aliases) {
                const value = currentSource?.[alias];

                if (value !== undefined && value !== null) {
                    return normalizeValue(value);
                }
            }
        }

        return "";
    }

    function syncQueryField(queryParams, key, value, setState) {
        if (hasUsableQueryValue(queryParams, key) || !value) {
            return false;
        }

        if (setState) {
            setState(value);
        }

        queryParams.set(key, value);
        return true;
    }

    function replaceCurrentSearch(queryParams) {
        history.replace({
            pathname: location.pathname,
            search: `?${queryParams.toString()}`
        });
    }

    function getDistributorClientNoFromPolicyLives(policyLivesSource) {
        const distributorClientId = policyLivesSource
            .find(policyLive => policyLive && policyLive.distributorClientId != null)
            ?.distributorClientId;

        return distributorClientId != null ? distributorClientId.toString() : "";
    }

    function syncDistributorClientNoQuery(distributorClientNumber) {
        const queryParams = new URLSearchParams(location.search);

        if (!syncQueryField(queryParams, "distributorClientNo", distributorClientNumber, setDistributorClientNo)) {
            return;
        }

        replaceCurrentSearch(queryParams);
    }

    function selectPolicySummaryData(policySummaryList) {
        if (!policyNumber) {
            return {
                policiesToSet: policySummaryList,
                selectedPolicySummary: policySummaryList[0],
                productCodeToSet: ""
            };
        }

        const policiesToSet = policySummaryList.filter(function (policy) {
            return policy.policyNumber === policyNumber;
        });
        const selectedPolicySummary = policiesToSet[0] || policySummaryList[0];

        return {
            policiesToSet,
            selectedPolicySummary,
            productCodeToSet: selectedPolicySummary?.productCode ?? selectedPolicySummary?.ProductCode ?? ""
        };
    }

    function resolveRequestEbqQueryFields(policySummaryItem, policyLivesSource = []) {
        if (!policySummaryItem) {
            return createEmptyRequestEbqFields();
        }

        const lives = policySummaryItem.lives ?? [];
        const owner = policySummaryItem.owner ?? {};
        const firstLife = getFirstItem(lives);
        const firstPolicyLive = getFirstItem(policyLivesSource);

        const distributorClientNo = getNormalizedValue(policySummaryItem, ["distributorClientNo"], firstLife, firstPolicyLive);
        const appRefNo = getNormalizedValue(policySummaryItem, ["appRefNo"]);
        const lifeNo = getNormalizedValue(policySummaryItem, ["lifeNo"], firstLife, firstPolicyLive);
        const productSeries = getNormalizedValue(policySummaryItem, ["productSeries"]);
        const clientId = getNormalizedValue(policySummaryItem, ["clientId"], owner, firstPolicyLive)
            || getNormalizedValue(owner, ["partyId"], firstPolicyLive);

        return {
            distributorClientNo,
            appRefNo,
            lifeNo,
            productSeries,
            clientId
        };
    }

    function resolveRequestEbqQueryFieldsFromPolicyDetails(policyDetailsItem) {
        if (!policyDetailsItem) {
            return createEmptyRequestEbqFields();
        }

        const policy = policyDetailsItem.policy ?? {};
        const product = policyDetailsItem.product ?? {};
        const lives = policyDetailsItem.lives ?? [];
        const owner = policy.policyOwner ?? {};
        const firstLife = getFirstItem(lives);

        const distributorClientNo = getNormalizedValue(firstLife, ["distributorClientNo"]);
        const appRefNo = getNormalizedValue(policy, ["appRefNo"]);
        const lifeNo = getNormalizedValue(firstLife, ["lifeNo"]);
        const productSeries = getNormalizedValue(product, ["productSeries"]);
        const clientId = getNormalizedValue(owner, ["clientId"]);

        return {
            distributorClientNo,
            appRefNo,
            lifeNo,
            productSeries,
            clientId
        };
    }

    function syncRequestExistingBusinessQuoteQueryParams(fields) {
        if (!isRequestExistingBusinessQuotePage) {
            return;
        }

        const queryParams = new URLSearchParams(location.search);
        let shouldReplaceQuery = false;

        shouldReplaceQuery = syncQueryField(queryParams, "distributorClientNo", fields.distributorClientNo, setDistributorClientNo) || shouldReplaceQuery;
        shouldReplaceQuery = syncQueryField(queryParams, "appRefNo", fields.appRefNo, setAppRefNo) || shouldReplaceQuery;
        shouldReplaceQuery = syncQueryField(queryParams, "lifeNo", fields.lifeNo) || shouldReplaceQuery;
        shouldReplaceQuery = syncQueryField(queryParams, "productSeries", fields.productSeries) || shouldReplaceQuery;
        shouldReplaceQuery = syncQueryField(queryParams, "clientId", fields.clientId, setClientId) || shouldReplaceQuery;

        if (shouldReplaceQuery) {
            replaceCurrentSearch(queryParams);
        }
    }

    function toggleDisplayExclusionWordingShort(pe) {
        var localPE = policyExclusion;

        localPE.forEach(e => {
            if (e.ID === pe.ID) {
                e.DisplayExclusionWordingShort = !e.DisplayExclusionWordingShort;
            }
        });

        setPolicyExclusion(localPE);
        triggerRefreshState();
    }

    async function retrievePolicyLives() {
        try {
            setSpinnerLoading(true);
            const result = await GetPolicyLives(
                policyNumber,
                distributorNo
            );

            if (result && result.length > 0) {
                setPolicyLives(result);
                setSpinnerLoading(false);
                if (shouldResolveDistributorClientFromPolicyLives) {
                    const fallbackDistributorClientNo = getDistributorClientNoFromPolicyLives(result);
                    syncDistributorClientNoQuery(fallbackDistributorClientNo);
                }

                return result;
            } else {
                throw Error(result.statusText);
            }
        }
        catch (error) {
            setToastBreadcrumb(true);
            setToastRecordDetails(true);
        }
        finally {
            setSpinnerLoading(false);
        }

        return null;
    }

    async function retrievePolicySummary(distributorClientNoOverride) {
        try {
            setSpinnerLoading(true);

            const distributorClientNumberToUse = distributorClientNoOverride || distributorClientNo;

            if (!distributorClientNumberToUse) {
                return;
            }

            const result = await GetPolicySummary(
                distributorClientNumberToUse
            );

            const policySummaryList = result?.policies || [];

            if (result && policySummaryList.length > 0) {
                const { policiesToSet, selectedPolicySummary, productCodeToSet } = selectPolicySummaryData(policySummaryList);
                const resolvedFields = resolveRequestEbqQueryFields(selectedPolicySummary, policyLives);

                syncRequestExistingBusinessQuoteQueryParams(resolvedFields);
                setProductCode(productCodeToSet);
                setPolicies(policiesToSet);

            } else {
                throw Error(result.statusText);
            }
        }
        catch (error) {
            console.log('error', error);
            setToastBreadcrumb(true);
            setToastTitleToast(true);
        }
        finally {
            setSpinnerLoading(false);
        }
    }

    async function retrievePolicyDetails() {

        try {
            setSpinnerLoading(true);

            const result = await GetPolicyDetails(
                policyNumber,
                distributorNo
            );

            if (result) {
                setPolicyDetails(result);
                setPolicyStatus(result.policy.status);

                const resolvedFields = resolveRequestEbqQueryFieldsFromPolicyDetails(result);
                syncRequestExistingBusinessQuoteQueryParams(resolvedFields);
            }
            else {
                throw Error(result.statusText);
            }
        }
        catch (error) {
            setToastPolicyDetails(true);
        }
        finally {
            setSpinnerLoading(false);
        }
    }

    async function retrieveCoverDetails() {

        try {
            setSpinnerLoading(true);

            const result = await GetCoverDetails(
                policyNumber,
                distributorNo
            );

            if (result) {
                setCoverDetails(result);
            }
            else {
                throw Error(result.statusText);
            }
        }
        catch (error) {
            setToastCoverDetails(true);
        }
        finally {
            setSpinnerLoading(false);
        }
    }

    async function retrievePaymentDetails() {

        try {
            setSpinnerLoading(true);

            const result = await GetPaymentDetails(
                policyNumber,
                distributorNo
            );

            if (result) {
                setPaymentDetails(result);
            }
            else {
                throw Error(result.statusText);
            }
        }
        catch (error) {
            setToastPaymentDetails(true);
        }
        finally {
            setSpinnerLoading(false);
        }
    }

    async function retrievePolicyExclusion() {

        try {
            setSpinnerLoading(true);

            const result = await GetPolicyExclusion(
                policyNumber,
                distributorNo
            );

            if (result !== undefined) {
                for (let i = 0; i < result.length; i++) {
                    if (result[i].exclusionWording !== undefined && result[i].exclusionWording !== null) {
                        if (result[i].exclusionWording.length > 60) {
                            result[i].exclusionWordingShort = result[i].exclusionWording.slice(0, 60) + " ...";
                        }
                        else {
                            result[i].exclusionWordingShort = result[i].exclusionWording;
                        }
                    }
                    else {
                        result[i].exclusionWordingShort = "";
                    }

                    result[i].DisplayExclusionWordingShort = true;
                    result[i].ID = i + 1;
                }
            }
            if (result) {
                setPolicyExclusion(result);
            }
            else {
                throw Error(result.statusText);
            }

        }
        catch (error) {
            setToastPolicyExclusion(true);
        }
        finally {
            setSpinnerLoading(false);
        }
    }

    async function retrieveBeneficiaries() {

        try {
            setSpinnerLoading(true);

            const result = await GetBeneficiaries(
                policyNumber
            );

            if (result) {
                setBeneficiaries(result);
            }
            else {
                throw Error(result.statusText);
            }
        }
        catch (error) {
            setToastBeneficiaries(true)
        }
        finally {
            setSpinnerLoading(false);
        }
    }

    useEffect(() => {
        async function retrieveDataInOrder() {
            try {
                const policyLivesResult = await retrievePolicyLives();

                let distributorClientNoForSummary = distributorClientNo;

                if (!distributorClientNoForSummary && policyLivesResult && policyLivesResult.length > 0) {
                    distributorClientNoForSummary = getDistributorClientNoFromPolicyLives(policyLivesResult);
                }

                await retrievePolicySummary(distributorClientNoForSummary);

                retrievePolicyDetails();
                retrieveCoverDetails();
                retrievePaymentDetails();
                retrievePolicyExclusion();
                retrieveBeneficiaries();
            }
            catch (error) {
                return (<div>error</div>);
            }
        }

        retrieveDataInOrder();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="policy-record" style={policyRecordStyle} data-testid="policyRecord">
            <GelThemeProvider>
                <GelScreenDetector>
                    <SideNav policyLives={policyLives} distributorClientNo={distributorClientNo} policies={policies} policyOrigin={policyOrigin} />
                    <GelContainerLite style={policyRecordSection}>
                        <GelRowLayout style={headerSection}>
                            <Header
                                policies={policies}
                                policyLives={policyLives}
                                clientId={clientId}
                                appRefNo={appRefNo}
                                distributorNo={distributorNo}
                                distributorClientNo={distributorClientNo}
                            />
                        </GelRowLayout>

                        <Spinner loading={spinnerLoading} />

                        <Switch>
                            <Route path="/dashboard/policy-details" render={({ match: { path } }) => (
                                <>
                                    <Route exact path={`${path}/`}>
                                        <div style={headerOverviewSection}>
                                            <GelRowLayout style={overviewSection}>
                                                <GelColumnLayout distribution="1/2 1/2" gutter="small">
                                                    <Overview policyDetails={policyDetails} policies={policies} />

                                                    <ActionButtons
                                                        beneficiaries={beneficiaries} policyDetails={policyDetails}
                                                        policies={policies}
                                                        certificateOfCurrency={certificateOfCurrency}
                                                        productCode={productCode || ""}
                                                    />
                                                </GelColumnLayout>
                                            </GelRowLayout>
                                        </div>

                                        <GelRowLayout style={recordDetailsSection}>
                                            <RecordDetails policyLives={policyLives} policyDetails={policyDetails} policies={policies} coverDetails={coverDetails} />
                                        </GelRowLayout>
                                    </Route>

                                    <Route path={`${path}/alter-policy`}>
                                        <GelRowLayout style={overviewSection}>
                                            <AlterPolicy
                                                distributorClientNo={distributorClientNo}
                                                distributorNo={distributorNo}
                                                policyNumber={policyNumber}
                                            />
                                        </GelRowLayout>
                                    </Route>
                                    <Route path={`${path}/view-correspondence`}>
                                        <GelRowLayout style={overviewSection}>
                                            <ViewCorrespondence />
                                        </GelRowLayout>
                                    </Route>

                                    <Route path={`${path}/payment-details`}>
                                        <GelRowLayout style={overviewSection}>
                                            <PaymentDetails paymentDetails={paymentDetails} />
                                        </GelRowLayout>
                                    </Route>

                                    <Route path={`${path}/policy-exclusions-and-loadings`}>
                                        <GelRowLayout style={overviewSection}>
                                            <PolicyExclusionTab
                                                policyExclusion={policyExclusion}
                                                policyLives={policyLives}
                                                coverDetails={coverDetails}
                                                policyDetails={policyDetails}
                                                toggleDisplayExclusionWordingShort={(p) => toggleDisplayExclusionWordingShort(p)}>
                                            </PolicyExclusionTab>
                                        </GelRowLayout>
                                    </Route>

                                    <Route path={`${path}/request-policy-documents`}>
                                        <GelRowLayout style={overviewSection}>
                                            <RequestPolicyDocuments
                                                distributorClientNo={distributorClientNo}
                                                distributorNo={distributorNo}
                                                policyNumber={policyNumber}
                                                policyDetails={policyDetails}
                                                adviserAccountEmail={adviserEmail}
                                                isStaff={isStaff} />
                                        </GelRowLayout>
                                    </Route>

                                    <Route path={`${url}/beneficiaries`}>
                                        <GelRowLayout style={overviewSection}>
                                            <Beneficiaries beneficiaries={beneficiaries} ></Beneficiaries>
                                        </GelRowLayout>
                                    </Route>

                                    <Route path={`${url}/request-existing-business-quote`}>
                                        <GelRowLayout style={overviewSection}>
                                            <RequestEBQ
                                                distributorClientNo={distributorClientNo}
                                                policyNumber={policyNumber}
                                                policyLives={policyLives}
                                                policyDetails={policyDetails}
                                                policies={policies}
                                                tlisQuoteFormURL={tlisQuoteFormURL} />
                                        </GelRowLayout>
                                    </Route>
                                    <Route path={`${url}/request-certificate-of-currency`}>
                                        <GelRowLayout style={overviewSection}>
                                            {/* <CertificateOfCurrency policyNumber={policyNumber} sourceSystem={sourceSystem}/> */}
                                            <CertificateOfCurrency
                                                policyNumber={policyNumber}
                                                policyDetails={policyDetails}
                                                distributorNo={distributorNo}
                                            />
                                        </GelRowLayout>
                                    </Route>

                                    <Route path={`${url}/request-policy-summary`}>
                                        <GelRowLayout style={overviewSection}>
                                            <PolicySummary
                                                policyNumber={policyNumber}
                                                policyDetails={policyDetails}
                                                distributorNo={distributorNo}
                                            />
                                        </GelRowLayout>
                                    </Route>
                                </>
                            )}>
                            </Route>
                        </Switch>
                    </GelContainerLite>
                </GelScreenDetector>
            </GelThemeProvider>

            {/* todo: rather than individual toast components for each message, 
        this should be extracted and have one toast component. 
        Only updating the message and state/type where necessary */}
            <div style={toastDiv} >
                <ToastMessage
                    toggleToast={toggleToastBreadcrumb}
                    toastType={toastFail}
                    showToast={toastBreadcrumb}
                    message={'Failed to load breadcrumb component'} />

                <ToastMessage
                    toggleToast={toggleToastPolicyDetails}
                    toastType={toastFail}
                    showToast={toastPolicyDetails}
                    message={'Failed to load policy details'} />

                <ToastMessage
                    toggleToast={toggleToastTitleToast}
                    toastType={toastFail}
                    showToast={toastTitleToast}
                    message={'Failed to load header component'} />

                <ToastMessage
                    toggleToast={toggleToastRecordDetails}
                    toastType={toastFail}
                    showToast={toastRecordDetails}
                    message={'Failed to load Insured Party details'} />

                <ToastMessage
                    toggleToast={toggleToastCoverDetails}
                    toastType={toastFail}
                    showToast={toastCoverDetails}
                    message={'Failed to load cover details'} />

                <ToastMessage
                    toggleToast={toggleToastPaymentDetails}
                    toastType={toastFail}
                    showToast={toastPaymentDetails}
                    message={'Failed to load payment details'} />

                <ToastMessage
                    toggleToast={toggleToastPolicyExclusion}
                    toastType={toastFail}
                    showToast={toastPolicyExclusion}
                    message={'Error retrieving Exclusions and Loadings details.'} />

                <ToastMessage
                    toggleToast={toggleToastBeneficiaries}
                    toastType={toastFail}
                    showToast={toastBeneficiaries}
                    message={'Failed to load Beneficiary details'} />
            </div>
        </div>

    );
}
